import { CabinTabId } from '@/constants/flights/cabinClass'
import { CabinClassSummary } from '@/types/PremiumEconomyApi'
import {
    CabinFareLike,
    getAvailableCabins,
    getDefaultSelectedCabin,
    getFareCabin,
    getMinPriceByCabin,
} from '@/utils/flights/cabinTabs'

type FareCardsLike = {
    tripFareCards?: CabinFareLike[] | null
    onwardFareCards?: CabinFareLike[] | null
    returnFareCards?: CabinFareLike[] | null
    tripCabinClassSummary?: CabinClassSummary | null
    onwardCabinClassSummary?: CabinClassSummary | null
    returnCabinClassSummary?: CabinClassSummary | null
    [key: string]: unknown
}

/** True when a summary has at least one cabin with non-empty cabinFareIds. */
export const isMeaningfulCabinClassSummary = (
    summary?: CabinClassSummary | null
): summary is CabinClassSummary =>
    !!summary &&
    typeof summary === 'object' &&
    Object.keys(summary).some(
        cabin => (summary[cabin]?.cabinFareIds?.length ?? 0) > 0
    )

/** BE sometimes sends `returnCabinClassSummary: {}` — treat as absent. */
export const normalizeCabinClassSummary = (
    summary?: CabinClassSummary | null
): CabinClassSummary | undefined => (isMeaningfulCabinClassSummary(summary) ? summary : undefined)

/** Itinerary fare cards price via `totalFareAmount`; cabinTabs helpers expect `pricing`. */
export const toCabinFareLike = (fare: CabinFareLike): CabinFareLike => {
    if (fare?.pricing?.totalPricing?.totalPrice != null) return fare
    const totalPrice = (fare as { totalFareAmount?: number })?.totalFareAmount
    if (typeof totalPrice !== 'number') return fare
    return {
        ...fare,
        pricing: {
            ...(fare.pricing || {}),
            totalPricing: {
                ...(fare.pricing?.totalPricing || {}),
                totalPrice,
            },
        },
    }
}

export const getItineraryLegFareCards = (
    fareCards: FareCardsLike | null | undefined,
    selectedTab: number,
    isSplRt: boolean
): CabinFareLike[] => {
    if (!fareCards) return []
    if (isSplRt) return fareCards.tripFareCards || []
    return (selectedTab === 0 ? fareCards.onwardFareCards : fareCards.returnFareCards) || []
}

export const getItineraryCabinClassSummary = (
    fareCards: FareCardsLike | null | undefined,
    selectedTab: number,
    isSplRt: boolean
): CabinClassSummary | undefined => {
    if (!fareCards) return undefined
    if (isSplRt) return normalizeCabinClassSummary(fareCards.tripCabinClassSummary)
    return normalizeCabinClassSummary(
        selectedTab === 0 ? fareCards.onwardCabinClassSummary : fareCards.returnCabinClassSummary
    )
}

/**
 * Same as SRP interstitial: fares drive display-cabin tabs first; summary keys
 * only while fares are missing. Keeps Indigo Upfront as its own tab.
 */
export const resolveAvailableItineraryCabins = (
    faresArray: CabinFareLike[],
    cabinClassSummary?: CabinClassSummary
): CabinTabId[] => getAvailableCabins({ faresArray, cabinClassSummary })

export const getItineraryCabinMatrix = ({
    fareCards,
    selectedTab,
    isSplRt,
}: {
    fareCards: FareCardsLike | null | undefined
    selectedTab: number
    isSplRt: boolean
}) => {
    const rawFares = getItineraryLegFareCards(fareCards, selectedTab, isSplRt)
    const faresArray = rawFares.map(toCabinFareLike)
    const cabinClassSummary = getItineraryCabinClassSummary(fareCards, selectedTab, isSplRt)
    const hasCabinMatrix = !!cabinClassSummary
    const availableCabins = resolveAvailableItineraryCabins(faresArray, cabinClassSummary)
    const priceByCabin = getMinPriceByCabin(
        faresArray,
        cabinClassSummary,
        false
    ) as Partial<Record<CabinTabId, number>>

    const selectedFare = faresArray.find(fare => fare?.selected)
    const selectedFareCabin = getFareCabin(selectedFare)
    const visibleCabins: CabinTabId[] =
        availableCabins.length > 0
            ? availableCabins
            : [
                  getDefaultSelectedCabin(selectedFareCabin, [
                      'ECONOMY',
                      'PREMIUM_ECONOMY',
                      'BUSINESS',
                      'FIRST',
                  ]),
              ]

    return {
        faresArray: rawFares,
        cabinClassSummary,
        hasCabinMatrix,
        availableCabins,
        priceByCabin,
        visibleCabins,
        selectedFareCabin: selectedFareCabin as CabinTabId | undefined,
    }
}

export const getInitialItineraryCabin = (
    fareCards: FareCardsLike | null | undefined,
    selectedTab: number,
    isSplRt: boolean
): CabinTabId => {
    const { visibleCabins, selectedFareCabin, faresArray } = getItineraryCabinMatrix({
        fareCards,
        selectedTab,
        isSplRt,
    })
    // Prefer the selected fare's *display* cabin so an Up Front selection reopens
    // on the Up Front tab, while theme stays ECONOMY.
    const selectedDisplay =
        faresArray.find(f => f?.selected)?.displayCabinType ||
        faresArray.find(f => f?.selected)?.displayCabinClass ||
        selectedFareCabin
    return getDefaultSelectedCabin(
        selectedDisplay ? String(selectedDisplay) : selectedFareCabin,
        visibleCabins
    )
}

const cabinTypesInList = (list?: CabinFareLike[] | null): Set<string> => {
    const set = new Set<string>()
    ;(list || []).forEach(fare => {
        const cabin = getFareCabin(fare)
        if (cabin) set.add(cabin)
    })
    return set
}

const mergeFareCardList = (
    existing?: CabinFareLike[] | null,
    incoming?: CabinFareLike[] | null
): CabinFareLike[] | null | undefined => {
    if (incoming === undefined) return existing
    if (!Array.isArray(incoming)) return incoming
    if (!Array.isArray(existing) || existing.length === 0) return incoming

    const existingIds = new Set(existing.map(fare => fare?.fareId).filter(Boolean) as string[])
    const sharesFareId = incoming.some(fare => fare?.fareId && existingIds.has(fare.fareId))
    // Different itinerary / unrelated payload — take incoming as authoritative.
    if (!sharesFareId) return incoming

    const existingCabins = cabinTypesInList(existing)
    const incomingCabins = cabinTypesInList(incoming)
    const lostCabin = [...existingCabins].some(cabin => !incomingCabins.has(cabin))
    if (!lostCabin && incoming.length >= existing.length) return incoming

    // Partial overwrite (e.g. fare-benefits-info) dropped PE/Business — union by fareId.
    const byId = new Map<string, CabinFareLike>()
    existing.forEach(fare => {
        if (fare?.fareId) byId.set(fare.fareId, fare)
    })
    incoming.forEach(fare => {
        if (!fare?.fareId) return
        byId.set(fare.fareId, { ...(byId.get(fare.fareId) || {}), ...fare })
    })

    const ordered: CabinFareLike[] = []
    const seen = new Set<string>()
    incoming.forEach(fare => {
        if (!fare?.fareId || seen.has(fare.fareId)) return
        ordered.push(byId.get(fare.fareId) || fare)
        seen.add(fare.fareId)
    })
    existing.forEach(fare => {
        if (!fare?.fareId || seen.has(fare.fareId)) return
        ordered.push(byId.get(fare.fareId) || fare)
        seen.add(fare.fareId)
    })
    return ordered
}

const pickCabinSummary = (
    existing?: CabinClassSummary | null,
    incoming?: CabinClassSummary | null
): CabinClassSummary | null | undefined => {
    if (isMeaningfulCabinClassSummary(incoming)) return incoming
    if (isMeaningfulCabinClassSummary(existing)) return existing
    if (incoming !== undefined) return incoming
    return existing
}

/**
 * Merge FARE_CARDS from a partial itinerary update without dropping the PE cabin
 * matrix. `fare-benefits-info` and some SELECT responses can return economy-only
 * cards / empty `*CabinClassSummary` and would otherwise wipe SSR create data.
 */
export const mergeFareCardsPreservingCabinMatrix = (
    existing?: FareCardsLike | null,
    incoming?: FareCardsLike | null
): FareCardsLike | null | undefined => {
    if (incoming == null) return existing
    if (existing == null || !Object.keys(existing).length) return incoming

    return {
        ...existing,
        ...incoming,
        onwardFareCards: mergeFareCardList(existing.onwardFareCards, incoming.onwardFareCards),
        returnFareCards: mergeFareCardList(existing.returnFareCards, incoming.returnFareCards),
        tripFareCards: mergeFareCardList(existing.tripFareCards, incoming.tripFareCards),
        onwardCabinClassSummary: pickCabinSummary(
            existing.onwardCabinClassSummary,
            incoming.onwardCabinClassSummary
        ),
        returnCabinClassSummary: pickCabinSummary(
            existing.returnCabinClassSummary,
            incoming.returnCabinClassSummary
        ),
        tripCabinClassSummary: pickCabinSummary(
            existing.tripCabinClassSummary,
            incoming.tripCabinClassSummary
        ),
    }
}

/** Shallow-merge itinerary components while preserving a richer FARE_CARDS cabin matrix. */
export const mergeItineraryDataPreservingFareCards = (
    existing: Record<string, unknown> | null | undefined,
    incoming: Record<string, unknown> | null | undefined
): Record<string, unknown> => {
    const base = existing || {}
    if (!incoming || !Object.keys(incoming).length) return base
    const next = { ...base, ...incoming }
    if ('FARE_CARDS' in incoming) {
        next.FARE_CARDS = mergeFareCardsPreservingCabinMatrix(
            base.FARE_CARDS as FareCardsLike,
            incoming.FARE_CARDS as FareCardsLike
        )
    }
    return next
}
